import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { IcoPencil, IcoUmbrella, IcoCheck, IcoX } from '../dashboard/DashIcons';

const STATUS_COLORS = {
  pending:  { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  approved: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
  denied:   { bg: '#fee2e2', color: '#991b1b', label: 'Denied' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{ padding: '0.2rem 0.6rem', borderRadius: '99px', fontSize: '0.72rem', fontWeight: '700', background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function TypeBadge({ type }) {
  return (
    <span style={{ padding: '0.2rem 0.55rem', borderRadius: '99px', fontSize: '0.7rem', fontWeight: '700', background: type === 'sick' ? '#fce7f3' : '#ede9fe', color: type === 'sick' ? '#9d174d' : '#5b21b6' }}>
      {type === 'sick' ? 'Sick' : 'PTO'}
    </span>
  );
}

function fmt(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const inputStyle = {
  width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px',
  border: '1px solid #d1d5db', fontSize: '0.875rem', color: '#111827',
  background: 'white', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
};
const labelStyle = { display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' };

// ── Balance card ──────────────────────────────────────────────────────────────
function BalanceBar({ label, used, allocated, carryover = 0 }) {
  const total = allocated + carryover;
  const pct   = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const remaining = Math.max(0, total - used);
  return (
    <div style={{ marginBottom: '0.6rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#374151' }}>{label}</span>
        <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
          {used}h used · <strong style={{ color: remaining < 8 ? '#ef4444' : '#10b981' }}>{remaining}h left</strong>
          {carryover > 0 && <span style={{ color: '#9ca3af' }}> · {carryover}h carried over</span>}
        </span>
      </div>
      <div style={{ height: '6px', background: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: pct > 85 ? '#ef4444' : '#22c55e', borderRadius: '99px', transition: 'width 0.4s' }} />
      </div>
      <p style={{ margin: '0.2rem 0 0', fontSize: '0.7rem', color: '#9ca3af' }}>{allocated}h allocated{carryover > 0 ? ` + ${carryover}h carryover` : ''}</p>
    </div>
  );
}

// ── Inline editable allocation ────────────────────────────────────────────────
function AllocationEditor({ balance, year, onSaved, authFetch }) {
  const [editing, setEditing]     = useState(false);
  const [pto, setPto]             = useState(balance.pto.allocated);
  const [sick, setSick]           = useState(balance.sick.allocated);
  const [ptoCo, setPtoCo]         = useState(balance.pto.carryover);
  const [sickCo, setSickCo]       = useState(balance.sick.carryover);
  const [ptoAdj, setPtoAdj]       = useState(balance.pto.usedAdjustment || 0);
  const [sickAdj, setSickAdj]     = useState(balance.sick.usedAdjustment || 0);
  const [saving, setSaving]       = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await authFetch(`/api/pto/balances/${balance.employeeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year,
          ptoAllocated:       parseFloat(pto)     || 0,
          sickAllocated:      parseFloat(sick)    || 0,
          ptoCarryover:       parseFloat(ptoCo)   || 0,
          sickCarryover:      parseFloat(sickCo)  || 0,
          ptoUsedAdjustment:  parseFloat(ptoAdj)  || 0,
          sickUsedAdjustment: parseFloat(sickAdj) || 0,
        }),
      });
      setEditing(false);
      onSaved();
    } catch {
      // non-fatal
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        style={{
          marginTop: '0.75rem',
          padding: '0.45rem 1rem',
          background: '#f0f4ff',
          color: '#4338ca',
          border: '1px solid #c7d2fe',
          borderRadius: '7px',
          cursor: 'pointer',
          fontSize: '0.8rem',
          fontWeight: '700',
        }}
      >
        <><IcoPencil size={14}/> Adjust PTO Balance</>
      </button>
    );
  }

  return (
    <div style={{ marginTop: '0.75rem', padding: '1rem', background: '#f8f9ff', borderRadius: '8px', border: '1px solid #e0e7ff' }}>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
        <strong>Allocated</strong> = total hours for the year. <strong>Carryover</strong> = hours from a prior period. <strong>Prior Used</strong> = hours already used outside ClockEarnings (e.g. before signing up).
      </p>

      <p style={{ margin: '0 0 0.3rem', fontSize: '0.72rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>PTO</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {[
          { label: 'Allocated (hrs)', val: pto,    set: setPto },
          { label: 'Carryover (hrs)', val: ptoCo,  set: setPtoCo },
          { label: 'Prior Used (hrs)', val: ptoAdj, set: setPtoAdj },
        ].map(({ label, val, set }) => (
          <div key={label}>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.72rem', color: '#374151', fontWeight: '600' }}>{label}</p>
            <input type="number" min="0" step="0.5" value={val} onChange={e => set(e.target.value)}
              style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
          </div>
        ))}
      </div>

      <p style={{ margin: '0 0 0.3rem', fontSize: '0.72rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sick Time</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {[
          { label: 'Allocated (hrs)', val: sick,    set: setSick },
          { label: 'Carryover (hrs)', val: sickCo,  set: setSickCo },
          { label: 'Prior Used (hrs)', val: sickAdj, set: setSickAdj },
        ].map(({ label, val, set }) => (
          <div key={label}>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.72rem', color: '#374151', fontWeight: '600' }}>{label}</p>
            <input type="number" min="0" step="0.5" value={val} onChange={e => set(e.target.value)}
              style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #c7d2fe', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={save} disabled={saving} style={{ padding: '0.45rem 1.1rem', borderRadius: '6px', background: '#22c55e', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '700' }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        <button onClick={() => setEditing(false)} style={{ padding: '0.45rem 1.1rem', borderRadius: '6px', background: '#4b5563', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PTOPage() {
  const { user, isSuperAdmin, authFetch } = useAuth();
  const isAdmin      = isSuperAdmin() || user?.role === 'adminassistant';
  const isManager    = user?.role === 'manager';
  const canViewBalances = isAdmin || isManager;

  const [tab, setTab]           = useState(canViewBalances ? 'balances' : 'requests');
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [policy, setPolicy]     = useState(null);
  const [employees, setEmployees] = useState([]);
  const [year, setYear]         = useState(new Date().getFullYear());
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const [form, setForm] = useState({
    employeeId: '', startDate: '', endDate: '', hours: '', reason: '', type: 'pto',
  });

  const fetchRequests = useCallback(async () => {
    try {
      const res  = await authFetch('/api/pto/requests');
      if (!res.ok) return;
      const data = await res.json();
      setRequests(data);
    } catch { setError('Failed to load requests.'); }
    finally { setLoading(false); }
  }, [authFetch]);

  const fetchBalances = useCallback(async () => {
    if (!canViewBalances) return;
    try {
      const res  = await authFetch(`/api/pto/balances?year=${year}`);
      if (!res.ok) return;
      const data = await res.json();
      setBalances(data.balances || []);
      setPolicy(data.policy);
    } catch { /* non-fatal */ }
  }, [canViewBalances, year, authFetch]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res  = await authFetch('/api/staff');
      if (!res.ok) return;
      const data = await res.json();
      setEmployees((data || []).filter(e => e.role !== 'superadmin'));
    } catch { /* non-fatal */ }
  }, [authFetch]);

  useEffect(() => {
    fetchRequests();
    fetchEmployees();
    if (canViewBalances) fetchBalances();
  }, [fetchRequests, fetchBalances, fetchEmployees, isAdmin, isManager]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.employeeId || !form.startDate || !form.endDate || !form.hours) return setError('Please fill in all required fields.');
    if (new Date(form.endDate) < new Date(form.startDate)) return setError('End date cannot be before start date.');
    setSubmitting(true);
    try {
      const res = await authFetch('/api/pto/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: form.employeeId,
          startDate: form.startDate,
          endDate: form.endDate,
          hours: parseFloat(form.hours),
          reason: form.reason,
          type: form.type,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to submit request.');
      setSuccess('Request submitted — the admin will be notified.');
      setForm({ employeeId: '', startDate: '', endDate: '', hours: '', reason: '', type: 'pto' });
      fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to submit request.');
    } finally { setSubmitting(false); }
  };

  const handleReview = async () => {
    if (!reviewModal) return;
    setReviewingId(reviewModal.requestId);
    setError('');
    try {
      const res = await authFetch(`/api/pto/requests/${reviewModal.requestId}/${reviewModal.action}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: reviewNotes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update request.');
      setSuccess(`Request ${reviewModal.action === 'approve' ? 'approved' : 'denied'} — manager notified.`);
      setReviewModal(null); setReviewNotes('');
      fetchRequests(); fetchBalances();
    } catch (err) {
      setError(err.message || 'Failed to update request.');
    } finally { setReviewingId(null); }
  };

  const pendingRequests  = requests.filter(r => r.status === 'pending');
  const resolvedRequests = requests.filter(r => r.status !== 'pending');

  const policyLabel = (p) => {
    if (!p) return '';
    if (p.calculationType === 'frontload') return `Front-load · ${p.frontloadHours}h/yr`;
    return `Accrual · ${p.accrualRate}h per ${p.accrualPeriod}`;
  };

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto', padding: '2rem 1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '800', color: '#111827' }}>PTO &amp; Sick Time</h1>
        <p style={{ margin: '0.3rem 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
          {isAdmin ? 'Manage balances and review requests.' : 'Submit time-off requests for yourself or your team.'}
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', background: '#f3f4f6', borderRadius: '10px', padding: '0.25rem', width: 'fit-content' }}>
        {canViewBalances && (
          <TabBtn label="Balances" active={tab === 'balances'} onClick={() => setTab('balances')} />
        )}
        <TabBtn label={`Pending${pendingRequests.length > 0 ? ` (${pendingRequests.length})` : ''}`} active={tab === 'pending'} onClick={() => setTab('pending')} />
        <TabBtn label="History" active={tab === 'history'} onClick={() => setTab('history')} />
        {isManager && (
          <TabBtn label="New Request" active={tab === 'new'} onClick={() => setTab('new')} />
        )}
      </div>

      {error   && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#991b1b', fontSize: '0.875rem' }}>{error}</div>}
      {success && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#166534', fontSize: '0.875rem' }}>{success}</div>}

      {/* ── Balances tab (superadmin) ── */}
      {tab === 'balances' && canViewBalances && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>Year:</label>
            <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ padding: '0.4rem 0.75rem', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}>
              {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            {policy && (
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.75rem', background: '#ede9fe', color: '#5b21b6', padding: '0.2rem 0.6rem', borderRadius: '99px', fontWeight: '600' }}>PTO: {policyLabel(policy.pto)}</span>
                <span style={{ fontSize: '0.75rem', background: '#fce7f3', color: '#9d174d', padding: '0.2rem 0.6rem', borderRadius: '99px', fontWeight: '600' }}>Sick: {policyLabel(policy.sick)}</span>
              </div>
            )}
          </div>

          {balances.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>No employees found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {balances.map(b => (
                <div key={b.employeeId} style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <p style={{ margin: '0 0 0.75rem', fontWeight: '700', fontSize: '0.95rem', color: '#111827' }}>{b.employeeName}</p>
                  <BalanceBar label="PTO"       used={b.pto.used}  allocated={b.pto.allocated}  carryover={b.pto.carryover} />
                  <BalanceBar label="Sick Time" used={b.sick.used} allocated={b.sick.allocated} carryover={b.sick.carryover} />
                  {isAdmin && <AllocationEditor balance={b} year={year} onSaved={fetchBalances} authFetch={authFetch} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Pending tab ── */}
      {tab === 'pending' && (
        <div>
          {loading ? <p style={{ color: '#6b7280' }}>Loading…</p>
            : pendingRequests.length === 0 ? (
              <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>No pending requests.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {pendingRequests.map(r => (
                  <RequestCard key={r._id} request={r} isAdmin={isAdmin} reviewingId={reviewingId}
                    onApprove={() => { setReviewModal({ requestId: r._id, action: 'approve' }); setReviewNotes(''); }}
                    onDeny={()    => { setReviewModal({ requestId: r._id, action: 'deny' });    setReviewNotes(''); }}
                  />
                ))}
              </div>
            )
          }
        </div>
      )}

      {/* ── History tab ── */}
      {tab === 'history' && (
        <div>
          {(isAdmin ? resolvedRequests : requests).length === 0 ? (
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>No history yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {(isAdmin ? resolvedRequests : requests).map(r => (
                <RequestCard key={r._id} request={r} isAdmin={isAdmin} reviewingId={reviewingId} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── New request tab (manager) ── */}
      {tab === 'new' && isManager && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <h2 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: '700', color: '#111827' }}>Submit a Request</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Type <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['pto', 'sick'].map(t => (
                    <button key={t} type="button"
                      onClick={() => setForm(f => ({ ...f, type: t }))}
                      style={{ flex: 1, padding: '0.55rem', borderRadius: '8px', border: `2px solid ${form.type === t ? '#22c55e' : '#e5e7eb'}`, background: form.type === t ? '#f0f4ff' : 'white', fontWeight: '700', fontSize: '0.82rem', color: form.type === t ? '#4338ca' : '#374151', cursor: 'pointer' }}>
                      {t === 'pto' ? <><IcoUmbrella size={14}/> PTO</> : 'Sick'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>For Employee <span style={{ color: '#ef4444' }}>*</span></label>
                <select value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))} style={inputStyle} required>
                  <option value="">Select employee…</option>
                  <option value={user?._id}>Myself ({user?.firstName} {user?.lastName})</option>
                  {employees.filter(e => String(e._id) !== String(user?._id)).map(e => (
                    <option key={e._id} value={e._id}>{e.firstName} {e.lastName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Start Date <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} style={inputStyle} required />
              </div>
              <div>
                <label style={labelStyle}>End Date <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} style={inputStyle} required />
              </div>
              <div>
                <label style={labelStyle}>Total Hours <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="number" min="0.5" step="0.5" placeholder="e.g. 8" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} style={inputStyle} required />
              </div>
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Reason (optional)</label>
              <textarea placeholder="Vacation, appointment, personal day…" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <button type="submit" disabled={submitting} style={{ background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', padding: '0.7rem 1.5rem', fontWeight: '700', fontSize: '0.9rem', cursor: submitting ? 'not-allowed' : 'pointer' }}>
              {submitting ? 'Submitting…' : 'Submit Request'}
            </button>
          </form>
        </div>
      )}

      {/* ── Review modal ── */}
      {reviewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>
              {reviewModal.action === 'approve' ? <><IcoCheck size={14}/> Approve Request</> : <><IcoX size={14}/> Deny Request</>}
            </h3>
            <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
              {reviewModal.action === 'approve' ? 'Manager will be notified by email and in-app.' : 'Manager will be notified with your reason.'}
            </p>
            <label style={labelStyle}>Notes {reviewModal.action === 'deny' ? '(explain why)' : '(optional)'}</label>
            <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={3} placeholder={reviewModal.action === 'deny' ? 'Please provide a reason…' : 'Any additional notes…'} style={{ ...inputStyle, resize: 'vertical', marginBottom: '1.25rem' }} />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setReviewModal(null)} style={{ padding: '0.6rem 1.25rem', borderRadius: '8px', border: 'none', background: '#4b5563', cursor: 'pointer', fontWeight: '600', color: 'white' }}>Cancel</button>
              <button onClick={handleReview} disabled={!!reviewingId} style={{ padding: '0.6rem 1.25rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700', color: 'white', background: reviewModal.action === 'approve' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)', opacity: reviewingId ? 0.6 : 1 }}>
                {reviewingId ? 'Saving…' : reviewModal.action === 'approve' ? 'Approve' : 'Deny'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: active ? '700' : '500', fontSize: '0.85rem', background: active ? 'white' : 'transparent', color: active ? '#111827' : '#6b7280', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
      {label}
    </button>
  );
}

function RequestCard({ request: r, isAdmin, reviewingId, onApprove, onDeny }) {
  const submitterName = r.requestedBy ? `${r.requestedBy.firstName} ${r.requestedBy.lastName}` : 'Unknown';
  return (
    <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: '200px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: '700', color: '#111827', fontSize: '0.95rem' }}>{r.employeeName}</span>
          {r.isForSelf && <span style={{ fontSize: '0.7rem', color: '#6b7280', background: '#f3f4f6', borderRadius: '99px', padding: '0.1rem 0.45rem' }}>self</span>}
          <TypeBadge type={r.type} />
          <StatusBadge status={r.status} />
        </div>
        {isAdmin && <p style={{ margin: '0 0 0.2rem', fontSize: '0.8rem', color: '#6b7280' }}>Submitted by {submitterName}</p>}
        <p style={{ margin: 0, fontSize: '0.82rem', color: '#374151' }}>
          {fmt(r.startDate)} – {fmt(r.endDate)} &nbsp;·&nbsp; <strong>{r.hours}h</strong>
        </p>
        {r.reason && <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>"{r.reason}"</p>}
        {r.reviewNotes && r.status !== 'pending' && <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>Note: {r.reviewNotes}</p>}
      </div>
      {isAdmin && r.status === 'pending' && (
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button onClick={onApprove} disabled={!!reviewingId} style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: 'none', background: '#10b981', color: 'white', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', opacity: reviewingId ? 0.5 : 1 }}>Approve</button>
          <button onClick={onDeny}    disabled={!!reviewingId} style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: '1px solid #fecaca', background: 'white', color: '#ef4444', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', opacity: reviewingId ? 0.5 : 1 }}>Deny</button>
        </div>
      )}
    </div>
  );
}
